import React from 'react';
import {
  Badge,
  Globe,
  Mail,
  Phone,
  CircleDot,
  User,
  Calendar,
  ShieldCheck,
  Link2,
  Building2,
} from 'lucide-react';

import './userFormSidebar.scss';

const NAVY = '#004195';

interface UserFormSidebarProps {
  name?: string;
  role?: string;
  employeeId?: string;
  email?: string;
  phone?: string;
  country?: string;
  isActive?: boolean;
  userId?: string;
  registrationDate?: string;
  verified?: boolean;
  partnerId?: string;
  partnerName?: string;
  isPartner?: boolean;
  companyName?: string;
  contactPerson?: string;
  agentId?: string;
  hideRegistrationDate?: boolean;
  hideVerificationRow?: boolean;
  hideStatusRow?: boolean;
}

const InfoRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="user-sidebar__info-row">
    <div className="user-sidebar__info-icon">
      <Icon size={16} strokeWidth={2} />
    </div>
    <div className="user-sidebar__info-content">
      <span className="user-sidebar__info-label">{label}</span>
      <span className="user-sidebar__info-value">{value || '—'}</span>
    </div>
  </div>
);

const UserFormSidebar = ({
  name,
  role,
  employeeId,
  email,
  phone,
  country,
  isActive,
  userId,
  registrationDate,
  verified,
  partnerId,
  partnerName,
  isPartner,
  companyName,
  contactPerson,
  agentId,
  hideRegistrationDate,
  hideVerificationRow,
  hideStatusRow,
}: UserFormSidebarProps) => {
  const initial = name?.trim()?.charAt(0)?.toUpperCase() || '?';

  const isAdminUser = !!role || !!employeeId || !!country;

  return (
    <aside className="user-sidebar">
      <div className="user-sidebar__header">
        <div className="user-sidebar__avatar">{initial}</div>
        <h3 className="user-sidebar__name">{name || '—'}</h3>
        {role && <span className="user-sidebar__role-badge">{role}</span>}
        {!role && !isPartner && (verified !== undefined || userId) && (
          <div className="user-sidebar__partner-badges">
            {verified !== undefined && (
              <span
                className={`user-sidebar__status-badge ${
                  verified
                    ? 'user-sidebar__status-badge--verified'
                    : 'user-sidebar__status-badge--unverified'
                }`}
              >
                {verified ? 'Verified' : 'Unverified'}
              </span>
            )}
            {userId && (
              <span className="user-sidebar__status-badge user-sidebar__status-badge--agent-id">
                {userId}
              </span>
            )}
          </div>
        )}
        {isPartner && (isActive !== undefined || agentId) && (
          <div className="user-sidebar__partner-badges">
            {isActive !== undefined && (
              <span
                className={`user-sidebar__status-badge ${
                  isActive
                    ? 'user-sidebar__status-badge--active'
                    : 'user-sidebar__status-badge--inactive'
                }`}
              >
                {isActive ? 'Active' : 'Inactive'}
              </span>
            )}
            {agentId && (
              <span className="user-sidebar__status-badge user-sidebar__status-badge--agent-id">
                {agentId}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="user-sidebar__divider" />

      <div className="user-sidebar__details">
        {isPartner ? (
          <>
            {companyName && <InfoRow icon={Building2} label="Company Name" value={companyName} />}
            {country && <InfoRow icon={Globe} label="Country Name" value={country} />}
            {contactPerson && <InfoRow icon={User} label="Contact Person" value={contactPerson} />}
            <InfoRow icon={Mail} label="Email ID" value={email} />
            <InfoRow icon={Phone} label="Mobile Number" value={phone} />
          </>
        ) : isAdminUser ? (
          <>
            <InfoRow icon={Badge} label="Employee ID" value={employeeId} />
            <InfoRow icon={Mail} label="Email" value={email} />
            <InfoRow icon={Phone} label="Phone" value={phone} />
            <InfoRow icon={Globe} label="Country" value={country} />
            <div className="user-sidebar__info-row">
              <div className="user-sidebar__info-icon">
                <CircleDot size={16} strokeWidth={2} />
              </div>
              <div className="user-sidebar__info-content">
                <span className="user-sidebar__info-label">Status</span>
                <span
                  className={`user-sidebar__status-badge ${
                    isActive
                      ? 'user-sidebar__status-badge--active'
                      : 'user-sidebar__status-badge--inactive'
                  }`}
                >
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <InfoRow icon={Mail} label="Email" value={email} />
            <InfoRow icon={Phone} label="Phone" value={phone} />
            {!hideRegistrationDate && registrationDate && (
              <InfoRow icon={Calendar} label="Registration Date" value={registrationDate} />
            )}
            {/* {partnerId && (
              <InfoRow
                icon={Link2}
                label="Referral Partner"
                value={
                  <span>
                    {partnerName && <strong style={{ color: NAVY }}>{partnerName}</strong>}
                    {partnerName && partnerId && <span style={{ margin: '0 6px' }}>·</span>}
                    <span style={{ color: '#64748b' }}>{partnerId}</span>
                  </span>
                }
              />
            )} */}
            {!hideVerificationRow && verified !== undefined && (
              <div className="user-sidebar__info-row">
                <div className="user-sidebar__info-icon">
                  <ShieldCheck size={16} strokeWidth={2} />
                </div>
                <div className="user-sidebar__info-content">
                  <span className="user-sidebar__info-label">Verification</span>
                  <span
                    className={`user-sidebar__status-badge ${
                      verified
                        ? 'user-sidebar__status-badge--verified'
                        : 'user-sidebar__status-badge--unverified'
                    }`}
                  >
                    {verified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
              </div>
            )}
            {!hideStatusRow && (
              <div className="user-sidebar__info-row">
                <div className="user-sidebar__info-icon">
                  <CircleDot size={16} strokeWidth={2} />
                </div>
                <div className="user-sidebar__info-content">
                  <span className="user-sidebar__info-label">Status</span>
                  <span
                    className={`user-sidebar__status-badge ${
                      isActive
                        ? 'user-sidebar__status-badge--active'
                        : 'user-sidebar__status-badge--inactive'
                    }`}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};

export default UserFormSidebar;
